// ─── Workshop Truck Availability Tracker ───
// NavBar – Fixed top navigation with tabs
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { NavLink } from 'react-router-dom'
import {
  Truck,
  RefreshCw,
  User,
  Clock,
  LayoutDashboard,
  BarChart2,
  Settings,
  DollarSign,
  History,
} from 'lucide-react'

interface NavBarProps {
  lastSynced: string | null
  isLoading: boolean
  onRefresh: () => void
}

const navTabs = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/trucks', label: 'All Trucks', icon: Truck },
  { to: '/report', label: 'Availability Report', icon: BarChart2 },
  { to: '/history', label: 'Status History', icon: History },
  { to: '/costs', label: 'Repair Costs', icon: DollarSign },
  { to: '/settings', label: 'Settings', icon: Settings },
]

function formatSyncTime(iso: string | null): string {
  if (!iso) return 'Never'
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-ZA', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
  } catch {
    return 'Never'
  }
}

export default function NavBar({ lastSynced, isLoading, onRefresh }: NavBarProps) {
  return (
    <header className="fixed top-0 left-0 right-0 z-40">
      {/* ── Top Bar ────────────────────────────────────── */}
      <div className="h-16 bg-[#1E2A3A] flex items-center justify-between px-6">
        {/* Left side */}
        <div className="flex items-center gap-3">
          <Truck className="text-[#3B82F6]" size={28} />
          <span className="text-white font-bold text-xl">IASL Workshop Fleet Tracker</span>
        </div>

        {/* Right side */}
        <div className="flex items-center gap-4">
          {/* Last synced */}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs">
            <Clock size={13} />
            <span>Synced: {formatSyncTime(lastSynced)}</span>
          </div>

          {/* Refresh button */}
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center gap-1.5 border border-gray-600 text-white text-sm
                       px-3 py-1.5 rounded-md hover:bg-white/5 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw
              size={14}
              className={isLoading ? 'animate-spin' : ''}
            />
            <span className="hidden sm:inline">
              {isLoading ? 'Syncing...' : 'Refresh'}
            </span>
          </button>

          {/* User chip */}
          <div className="flex items-center gap-2 bg-[#2D3F55] text-white text-sm
                          px-3 py-1 rounded-full">
            <User size={14} className="text-gray-400" />
            <span className="hidden sm:inline">Workshop Supervisor</span>
          </div>
        </div>
      </div>

      {/* ── Tab Navigation ─────────────────────────────── */}
      <nav className="bg-[#253347] px-6 flex gap-1 overflow-x-auto">
        {navTabs.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            className={({ isActive }) =>
              `flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors
               border-b-2 whitespace-nowrap ${isActive
                ? 'text-white border-blue-400'
                : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
              }`
            }
          >
            <Icon size={16} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </header>
  )
}
